// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

export type PlayerSearchResult = {
  id: string;
  name: string;
  userId?: string | null;
};

export type PlayerSearchState = {
  results: PlayerSearchResult[];
  searching: boolean;
  failed: boolean;
};

const DEBOUNCE_MS = 300;

const emptyState: PlayerSearchState = { results: [], searching: false, failed: false };

/**
 * Finds players to add, beyond the friends and own guests already loaded. The server applies each
 * user's search-visibility preference, so this never surfaces someone who opted out; matching is on
 * the user's name or an exact Golf ID.
 *
 * Returns nothing for an empty query — the pickers show the caller's known players in that case, and
 * the whole platform is deliberately not enumerable.
 */
export function usePlayerSearch(
  apiBaseUrl: string,
  token: string | null | undefined,
  query: string
): PlayerSearchState {
  const [state, setState] = useState<PlayerSearchState>(emptyState);

  useEffect(() => {
    const trimmed = query.trim();
    if (!token || trimmed.length === 0) {
      setState(emptyState);
      return;
    }

    let cancelled = false;
    setState((previous) => ({ ...previous, searching: true, failed: false }));

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players/search?q=${encodeURIComponent(trimmed)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`players.search ${response.status}`);
        }
        const payload = (await response.json()) as Array<Record<string, unknown>>;
        if (cancelled) {
          return;
        }
        setState({
          results: (payload ?? [])
            .map((entry) => ({
              id: ((entry.id as string | undefined) ?? (entry.playerId as string | undefined) ?? '').trim(),
              name: ((entry.name as string | undefined) ?? '').trim(),
              userId: (entry.userId as string | null | undefined) ?? null,
            }))
            .filter((entry) => entry.id.length > 0 && entry.name.length > 0),
          searching: false,
          failed: false,
        });
      } catch {
        if (!cancelled) {
          setState({ results: [], searching: false, failed: true });
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [apiBaseUrl, query, token]);

  return state;
}
